/**
 * File Explorer API wrapper.
 *
 * Thin typed shims over `/api/fs/*`. The Python service emits snake_case
 * JSON; the schemas in `@chaos/types/fs` mirror that verbatim. Consumers
 * read the raw field names directly so we don't need a separate camel-
 * case translation layer.
 */
import type {
  FsReadResponse,
  FsScopesResponse,
  FsSearchResponse,
  FsTreeNode,
} from '@chaos/types/fs';
import { env } from '../../env';
import { apiJson } from './client';

interface ListOptions {
  path: string;
}

interface ReadOptions {
  path: string;
  offset?: number;
  limit?: number;
}

interface SearchOptions {
  q: string;
  root?: string;
  limit?: number;
}

interface UploadOptions {
  folder: string;
  file: File;
  overwrite?: boolean;
  signal?: AbortSignal;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export const fsApi = {
  scopes(): Promise<FsScopesResponse> {
    return apiJson<FsScopesResponse>('/api/fs/scopes');
  },

  list(opts: ListOptions): Promise<FsTreeNode> {
    return apiJson<FsTreeNode>(`/api/fs/list?${buildQuery({ path: opts.path })}`);
  },

  stat(opts: { path: string }): Promise<FsTreeNode> {
    return apiJson<FsTreeNode>(`/api/fs/stat?${buildQuery({ path: opts.path })}`);
  },

  read(opts: ReadOptions): Promise<FsReadResponse> {
    return apiJson<FsReadResponse>(
      `/api/fs/read?${buildQuery({
        path: opts.path,
        offset: opts.offset,
        limit: opts.limit,
      })}`,
    );
  },

  /**
   * Build the absolute download URL so `<img>`, `<video>`, `<embed>`,
   * etc. can fetch the binary directly (cookies are sent because the
   * page origin matches).
   */
  downloadUrl(path: string): string {
    return `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/api/fs/download?${buildQuery({ path })}`;
  },

  search(opts: SearchOptions): Promise<FsSearchResponse> {
    return apiJson<FsSearchResponse>(
      `/api/fs/search?${buildQuery({
        q: opts.q,
        root: opts.root,
        limit: opts.limit,
      })}`,
    );
  },

  /**
   * Multipart upload using XMLHttpRequest so we can stream progress
   * updates back to the caller. `fetch` doesn't expose upload progress
   * without a streaming Body — and Safari still lacks support for it.
   */
  upload(opts: UploadOptions): Promise<FsTreeNode> {
    return new Promise<FsTreeNode>((resolve, reject) => {
      const form = new FormData();
      form.set('folder', opts.folder);
      form.set('overwrite', opts.overwrite ? 'true' : 'false');
      form.set('file', opts.file, opts.file.name);

      const xhr = new XMLHttpRequest();
      const url = `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/api/fs/upload`;
      xhr.open('POST', url);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (!opts.onProgress) return;
        if (!e.lengthComputable) return;
        opts.onProgress({ loaded: e.loaded, total: e.total });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as FsTreeNode);
          } catch (err) {
            reject(err instanceof Error ? err : new Error('Could not parse upload response'));
          }
          return;
        }
        const err = new Error(`Upload failed (${xhr.status})`);
        (err as Error & { status?: number; body?: string }).status = xhr.status;
        (err as Error & { status?: number; body?: string }).body = xhr.responseText;
        reject(err);
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));

      if (opts.signal) {
        const onAbort = (): void => xhr.abort();
        opts.signal.addEventListener('abort', onAbort, { once: true });
      }

      xhr.send(form);
    });
  },
};

export const fsKeys = {
  all: ['fs'] as const,
  scopes: () => [...fsKeys.all, 'scopes'] as const,
  list: (path: string) => [...fsKeys.all, 'list', path] as const,
  read: (path: string, offset: number, limit: number) =>
    [...fsKeys.all, 'read', path, offset, limit] as const,
  search: (q: string, root: string | undefined) => [...fsKeys.all, 'search', q, root] as const,
};
